import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../../../contexts/AuthContext';
import { usePublicTheme } from '../../../../contexts/PublicThemeContext';
import LanguageSwitcher from '../../../../shared/components/LanguageSwitcher/LanguageSwitcher';
import GooeyNav from './GooeyNav';
import './Navbar.css';
import './GooeyNav.css';

const Navbar: React.FC = () => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [scrolled, setScrolled] = useState<boolean>(false);
  const location = useLocation();
  const { isAuthenticated, user, logout } = useAuth();
  const navigate = useNavigate();
  const { theme, toggleTheme } = usePublicTheme();
  const mobileMenuRef = useRef<HTMLDivElement>(null);
  const particleRefs = useRef<Map<string, HTMLSpanElement>>(new Map());

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setIsOpen(false);
  }, [location]);

  const handleLogout = () => {
    logout();
    navigate('/');
    setIsOpen(false);
  };

  // Particles animation for mobile menu (similar to GooeyNav)
  const createMobileParticles = (element: HTMLElement) => {
    const particleCount = 10;
    const noise = (n = 1) => n / 2 - Math.random() * n;
    
    const getXY = (distance: number, pointIndex: number, totalPoints: number) => {
      const angle = ((360 + noise(8)) / totalPoints) * pointIndex * (Math.PI / 180);
      return [distance * Math.cos(angle), distance * Math.sin(angle)];
    };

    for (let i = 0; i < particleCount; i++) {
      const t = 800 + noise(200);
      const start = getXY(30, particleCount - i, particleCount);
      const end = getXY(5 + noise(5), particleCount - i, particleCount);
      const rotate = noise(50);
      const scale = 1 + noise(0.2);
      const color = [1, 2, 3, 1, 2, 3, 1, 4][Math.floor(Math.random() * 8)];

      setTimeout(() => {
        const particle = document.createElement('span');
        const point = document.createElement('span');
        particle.classList.add('mobile-particle');
        particle.style.setProperty('--start-x', `${start[0]}px`);
        particle.style.setProperty('--start-y', `${start[1]}px`);
        particle.style.setProperty('--end-x', `${end[0]}px`);
        particle.style.setProperty('--end-y', `${end[1]}px`);
        particle.style.setProperty('--time', `${t}ms`);
        particle.style.setProperty('--scale', `${scale}`);
        particle.style.setProperty('--color', `var(--color-${color}, white)`);
        particle.style.setProperty('--rotate', `${rotate}deg`);

        point.classList.add('mobile-point');
        particle.appendChild(point);
        element.appendChild(particle);

        requestAnimationFrame(() => {
          element.classList.add('particles-active');
        });

        setTimeout(() => {
          try {
            element.removeChild(particle);
          } catch {
            // Do nothing
          }
        }, t);
      }, 30 * i);
    }
  };

  const handleMobileNavClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    const link = e.currentTarget;
    const particles = link.querySelectorAll('.mobile-particle');
    particles.forEach(p => link.removeChild(p));
    
    createMobileParticles(link);
    setIsOpen(false);
  };

  // Determine active index based on current location
  const getActiveIndex = () => {
    const path = location.pathname;
    if (path === '/') return 0;
    if (path === '/features') return 1;
    if (path === '/about') return 2;
    if (path === '/pricing') return 3;
    if (path === '/community' || path.startsWith('/community/')) return 4;
    if (path === '/support') return 5;
    return 0;
  };

  // Navigation items for GooeyNav (labels translated)
  const navItems = useMemo(() => [
    { label: t('nav.home'), href: '/' },
    { label: t('nav.features'), href: '/features' },
    { label: t('nav.about'), href: '/about' },
    { label: t('nav.pricing'), href: '/pricing' },
    { label: t('nav.community'), href: '/community' },
    { label: t('nav.support'), href: '/support' },
  ], [t]);

  return (
    <nav className={`navbar ${scrolled ? 'scrolled' : ''}`}>
      <div className="container">
        <div className="nav-wrapper">
          <Link to="/" className="logo">
            <img 
              src="/images/CRYPXA_Navbar_public_Logo.png" 
              alt="Crypxa Logo" 
              className="logo-img"
            />
          </Link>
          
          {/* GooeyNav for main navigation */}
          <div className="gooey-nav-wrapper">
            <GooeyNav 
              items={navItems}
              initialActiveIndex={getActiveIndex()}
              particleCount={15}
              animationTime={600}
            />
          </div>

          {/* Auth buttons */}
          <div className="nav-auth-section">
            {location.pathname === '/subscription-selection' ? (
              <>
                <Link to="/login" className="nav-link nav-link-login">
                  <i className="fas fa-sign-in-alt"></i> {t('nav.login')}
                </Link>
                <Link to="/signup" className="nav-link nav-link-signup">
                  <i className="fas fa-user-plus"></i> {t('nav.signUp')}
                </Link>
              </>
            ) : isAuthenticated ? (
              <>
                <Link to="/dashboard" className="nav-link nav-link-dashboard">
                  <i className="fas fa-tachometer-alt"></i> {t('nav.dashboard')}
                </Link>
                <button 
                  onClick={handleLogout} 
                  className="nav-link nav-link-logout"
                >
                  <i className="fas fa-sign-out-alt"></i> {t('nav.logout')}
                </button>
              </>
            ) : (
              <>
                <Link to="/login" className="nav-link nav-link-login">
                  <i className="fas fa-sign-in-alt"></i> {t('nav.login')}
                </Link>
                <Link to="/signup" className="nav-link nav-link-signup">
                  <i className="fas fa-user-plus"></i> {t('nav.signUp')}
                </Link>
              </>
            )}
            {location.pathname !== '/subscription-selection' && (
              <LanguageSwitcher variant="public" />
            )}
            {/* Theme Toggle - Hidden on subscription selection */}
            {location.pathname !== '/subscription-selection' && (
              <button 
                className="theme-toggle-btn" 
                onClick={toggleTheme}
                aria-label={`Switch to ${theme === 'light' ? 'dark' : 'light'} theme`}
                title={`Switch to ${theme === 'light' ? 'dark' : 'light'} theme`}
              >
                {theme === 'light' ? (
                  <i className="fas fa-moon"></i>
                ) : (
                  <i className="fas fa-sun"></i>
                )}
              </button>
            )}
          </div>

          <div 
            className={`hamburger ${isOpen ? 'active' : ''}`}
            onClick={() => setIsOpen(!isOpen)}
          >
            <span></span>
            <span></span>
            <span></span>
          </div>
        </div>

        {/* Mobile Menu */}
        <div className={`mobile-menu ${isOpen ? 'active' : ''}`}>
          <div className="mobile-menu-content">
            <div className="mobile-nav-section">
              <Link 
                to="/" 
                className={`mobile-nav-link ${location.pathname === '/' ? 'active' : ''}`}
                onClick={handleMobileNavClick}
              >
                <i className="fas fa-home"></i> 
                <span>{t('nav.home')}</span>
                {location.pathname === '/' && <span className="mobile-nav-indicator"></span>}
              </Link>
              <Link 
                to="/features" 
                className={`mobile-nav-link ${location.pathname === '/features' ? 'active' : ''}`}
                onClick={handleMobileNavClick}
              >
                <i className="fas fa-star"></i> 
                <span>Features</span>
                {location.pathname === '/features' && <span className="mobile-nav-indicator"></span>}
              </Link>
              <Link 
                to="/about" 
                className={`mobile-nav-link ${location.pathname === '/about' ? 'active' : ''}`}
                onClick={handleMobileNavClick}
              >
                <i className="fas fa-info-circle"></i> 
                <span>{t('nav.about')}</span>
                {location.pathname === '/about' && <span className="mobile-nav-indicator"></span>}
              </Link>
              <Link 
                to="/pricing" 
                className={`mobile-nav-link ${location.pathname === '/pricing' ? 'active' : ''}`}
                onClick={handleMobileNavClick}
              >
                <i className="fas fa-dollar-sign"></i> 
                <span>Pricing</span>
                {location.pathname === '/pricing' && <span className="mobile-nav-indicator"></span>}
              </Link>
              <Link 
                to="/community" 
                className={`mobile-nav-link ${location.pathname === '/community' || location.pathname.startsWith('/community/') ? 'active' : ''}`}
                onClick={handleMobileNavClick}
              >
                <i className="fas fa-comments"></i> 
                <span>{t('nav.community')}</span>
                {(location.pathname === '/community' || location.pathname.startsWith('/community/')) && <span className="mobile-nav-indicator"></span>}
              </Link>
              <Link 
                to="/support" 
                className={`mobile-nav-link ${location.pathname === '/support' ? 'active' : ''}`}
                onClick={handleMobileNavClick}
              >
                <i className="fas fa-envelope"></i> 
                <span>{t('nav.support')}</span>
                {location.pathname === '/support' && <span className="mobile-nav-indicator"></span>}
              </Link>
            </div>
            
            <div className="mobile-menu-divider"></div>
            
            <div className="mobile-auth-section">
              {isAuthenticated ? (
                <>
                  <Link 
                    to="/dashboard" 
                    className="mobile-nav-link mobile-nav-link-dashboard"
                    onClick={() => setIsOpen(false)}
                  >
                    <i className="fas fa-tachometer-alt"></i> 
                    <span>{t('nav.dashboard')}</span>
                  </Link>
                  <button 
                    onClick={() => {
                      handleLogout();
                      setIsOpen(false);
                    }}
                    className="mobile-nav-link mobile-nav-link-logout"
                  >
                    <i className="fas fa-sign-out-alt"></i> 
                    <span>{t('nav.logout')}</span>
                  </button>
                </>
              ) : (
                <>
                  <Link 
                    to="/login" 
                    className="mobile-nav-link mobile-nav-link-login"
                    onClick={() => setIsOpen(false)}
                  >
                    <i className="fas fa-sign-in-alt"></i> 
                    <span>{t('nav.login')}</span>
                  </Link>
                  <Link 
                    to="/signup" 
                    className="mobile-nav-link mobile-nav-link-signup"
                    onClick={() => setIsOpen(false)}
                  >
                    <i className="fas fa-user-plus"></i> 
                    <span>{t('nav.signUp')}</span>
                  </Link>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </nav>
  );
};

export default Navbar;

